import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select as WFSelect, type SelectProps } from '@wf/components';

import { type LicenseFieldProps } from './LicenseFieldController';

import { useLicenseController } from '../../hooks/useLicenseController';
import { HeaderTabFields } from '../../types/fieldEnums';

const enumSelectFields = { [HeaderTabFields.typeOfUse]: HeaderTabFields.typeOfUse };

export interface LicenseSelectFieldAdditionalProps extends SelectProps<SelectOption>, LicenseFieldProps {
  maxCharacters?: number;
}

export const LicenseSelectField = ({
  name,
  defaultValue,
  configName,
  featureFlag,
  viewMode,
  rules,
  shouldUnregister,
  control,
  isClearable = true,
  disabled,
  maxCharacters,
  ...rest
}: LicenseSelectFieldAdditionalProps & UseControllerProps<FieldValues, any>) => {
  const { control: contextControl } = useFormContext();
  const { t } = useTranslation();

  const {
    field: { value, onChange, onBlur, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultValue ?? null,
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const handleChange: typeof rest.onChange = (...args) => {
    onChange(...args);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(...args);
    }
  };

  const handleBlur: typeof rest.onBlur = (...args) => {
    onBlur();

    if ('onBlur' in rest && typeof rest.onBlur === 'function') {
      rest.onBlur(...args);
    }
  };

  const handleKeyDown: typeof rest.onKeyDown = (event) => {
    const { key, target } = event;

    if (
      maxCharacters &&
      (value?.map((i: SelectOption) => i.value).join('').length >= maxCharacters ||
        (target as HTMLInputElement).value.length >= maxCharacters) &&
      key !== 'Backspace' &&
      key !== 'Enter' &&
      key !== 'Delete'
    ) {
      event.preventDefault();
    }

    if ('onKeyDown' in rest && typeof rest.onKeyDown === 'function') {
      rest.onKeyDown(event);
    }
  };

  if (config.isHidden) {
    return <></>;
  }

  const getTranslatedValue = () => {
    if (name in enumSelectFields && value) {
      if (Array.isArray(value)) {
        return value.map((v) => ({
          ...v,
          label: t(v.value),
        }));
      }

      return { ...value, label: t(value.value) };
    }

    if (Array.isArray(value)) {
      return value.map((v) => ({ ...v, label: t(v.value) }));
    }

    return value;
  };

  return (
    <WFSelect
      {...fieldRest}
      {...computedProps}
      {...rest}
      onKeyDown={maxCharacters ? handleKeyDown : rest.onKeyDown}
      value={getTranslatedValue()}
      isClearable={isClearable}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
